import { useChat } from "../../../Context/ChatContext";
import { useData } from "../../../Context/DataContext";
import { Plus, MessageSquare, Trash2, ChevronLeft, ChevronRight } from "react-feather";
import { useState } from "react";
import welcomeLogo from "../../../Assets/welcome-logo.png";

interface ChatHistorySidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

const ChatHistorySidebar = ({ isCollapsed, onToggle }: ChatHistorySidebarProps) => {
  const { sessions, currentSessionId, loadSession, deleteSession, clearChat } = useChat();
  const { currentTimeZone } = useData();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleNewChat = async () => {
    clearChat();
  };

  const handleLoadSession = async (sessionId: string) => {
    if (sessionId !== currentSessionId) {
      await loadSession(sessionId);
    }
  };

  const handleDeleteSession = async (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation();
    setDeletingId(sessionId);
    await deleteSession(sessionId);
    setDeletingId(null);
  };

  // Format date for display using user's timezone
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';

    // Parse the UTC timestamp and get date parts in user's timezone
    const date = new Date(dateString + (dateString.includes('Z') || dateString.includes('+') ? '' : 'Z'));
    const dateOptions: Intl.DateTimeFormatOptions = { timeZone: currentTimeZone, year: 'numeric', month: 'numeric', day: 'numeric' };
    const dateParts = new Intl.DateTimeFormat('en-US', dateOptions).formatToParts(date);
    const dateDay = parseInt(dateParts.find(p => p.type === 'day')?.value || '0');
    const dateMonth = parseInt(dateParts.find(p => p.type === 'month')?.value || '0') - 1;
    const dateYear = parseInt(dateParts.find(p => p.type === 'year')?.value || '0');

    // Get today's date parts in user's timezone
    const now = new Date();
    const nowParts = new Intl.DateTimeFormat('en-US', dateOptions).formatToParts(now);
    const nowDay = parseInt(nowParts.find(p => p.type === 'day')?.value || '0');
    const nowMonth = parseInt(nowParts.find(p => p.type === 'month')?.value || '0') - 1;
    const nowYear = parseInt(nowParts.find(p => p.type === 'year')?.value || '0');

    // Calculate difference in days
    const todayDate = new Date(nowYear, nowMonth, nowDay);
    const sessionDate = new Date(dateYear, dateMonth, dateDay);
    const diffDays = Math.floor((todayDate.getTime() - sessionDate.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays >= 2 && diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { timeZone: currentTimeZone });
  };

  // Group sessions by date
  const groupedSessions = sessions.reduce((groups: { [key: string]: typeof sessions }, session) => {
    const dateLabel = formatDate(session.updated_at);
    if (!groups[dateLabel]) {
      groups[dateLabel] = [];
    }
    groups[dateLabel].push(session);
    return groups;
  }, {});

  return (
    <div
      className={`flex flex-col absolute left-0 top-0 bottom-0 z-20 transition-all duration-200 ease-in-out ${
        isCollapsed ? 'w-12' : 'w-64 bg-dark-card border-r border-dark-border'
      }`}
    >
      {isCollapsed ? (
        // Collapsed state
        <>
          <button
            onClick={onToggle}
            className="p-2 hover:bg-dark-card-hover rounded-lg transition-colors mt-4 mx-auto"
            title="Expand sidebar"
          >
            <ChevronRight size={20} className="text-gray-400" />
          </button>
          <button
            onClick={handleNewChat}
            className="p-2 hover:bg-dark-card-hover rounded-lg transition-colors mt-2 mx-auto"
            title="New chat"
          >
            <Plus size={20} className="text-gray-400" />
          </button>
        </>
      ) : (
        // Expanded state
        <>
          {/* Header */}
          <div className="p-3 border-b border-dark-border flex items-center justify-between">
            <div className="flex items-center gap-0.5">
              <img src={welcomeLogo} alt="Intellachat" className="w-8 h-8" />
              <span className="text-sm font-medium text-white">Intellachat</span>
            </div>
            <button
              onClick={onToggle}
              className="p-1.5 hover:bg-dark-card-hover rounded-lg transition-colors"
              title="Collapse sidebar"
            >
              <ChevronLeft size={18} className="text-gray-400" />
            </button>
          </div>

          {/* New Chat Button */}
          <div className="p-3">
            <button
              onClick={handleNewChat}
              className="w-full flex items-center gap-2 px-3 py-2.5 bg-dark-card-hover hover:bg-dark-border rounded-lg transition-colors text-white text-sm font-medium"
            >
              <Plus size={18} />
              <span>New chat</span>
            </button>
          </div>

          {/* Sessions List */}
          <div className="flex-1 overflow-y-auto px-2 pb-4">
            {Object.entries(groupedSessions).map(([dateLabel, dateSessions]) => (
              <div key={dateLabel} className="mb-4">
                <div className="px-2 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {dateLabel}
                </div>
                {dateSessions.map((session) => (
                  <button
                    key={session.session_id}
                    onClick={() => handleLoadSession(session.session_id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-left group ${
                      currentSessionId === session.session_id
                        ? 'bg-dark-card-hover text-white'
                        : 'text-gray-300 hover:bg-dark-card-hover hover:text-white'
                    }`}
                  >
                    <MessageSquare size={16} className="flex-shrink-0 text-gray-500" />
                    <span className="flex-1 text-sm truncate">
                      {session.title || 'New Chat'}
                    </span>
                    <button
                      onClick={(e) => handleDeleteSession(e, session.session_id)}
                      className={`p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-500/20 transition-all ${
                        deletingId === session.session_id ? 'opacity-100' : ''
                      }`}
                      title="Delete chat"
                    >
                      {deletingId === session.session_id ? (
                        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Trash2 size={14} className="text-gray-400 hover:text-red-400" />
                      )}
                    </button>
                  </button>
                ))}
              </div>
            ))}

            {sessions.length === 0 && (
              <div className="px-3 py-8 text-center text-gray-500 text-sm">
                No chat history yet.
                <br />
                Start a new conversation!
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ChatHistorySidebar;
